'use client';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { CartApiItem, CartItemPayload, CartItemRef, CartResponse, ID, OfferStatus, Product } from '@nutralab/api';

import { useAuth } from '../AuthProvider';
import { useSiteConfig } from '../SiteConfigProvider';

import {
    addCartItems,
    type CartActionResult,
    getCart,
    removeCartItems,
    updateCartItems,
} from '@/shared/api/actions/cart';
import { ANALYTICS_GOALS } from '@/shared/constants';
import { getCartKey, getCatalogItemPath, getImageUrl, reachGoal } from '@/shared/utils';

export interface CartItem extends CartItemRef {
    /** `getCartKey(item)`: the key of `items`, `pendingKeys`. */
    key: string;
    title: string;
    description: string;
    price: string;
    finalPrice: string;
    subtotal: number;
    total: number;
    imageSrc: string;
    quantity: number;
    orderable: boolean;
    status: OfferStatus | null;
    href: string | null;
}

interface CartContextValue {
    /** By `getCartKey`. */
    items: Map<string, CartItem>;
    isReady: boolean;
    pendingKeys: Set<string>;
    addItem: (ref: CartItemRef) => void;
    removeItem: (ref: CartItemRef) => void;
    removeItems: (refs: CartItemRef[]) => void;
    updateQuantity: (ref: CartItemRef, quantity: number) => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export const COOKIE_NAME = 'cart';
const STORAGE_KEY = 'cart_items';

interface StoredCartItem extends CartItemRef {
    quantity: number;
}

/** The guest cart by `getCartKey`. */
type Quantities = Map<string, StoredCartItem>;

function isStoredCartItem(value: unknown): value is { id: ID; quantity: number; type?: unknown } {
    return (
        typeof value === 'object' &&
        value !== null &&
        'id' in value &&
        typeof value.id === 'number' &&
        'quantity' in value &&
        typeof value.quantity === 'number'
    );
}

function readStoredCart(): Quantities {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : [];
        const stored = Array.isArray(parsed) ? parsed.filter(isStoredCartItem) : [];
        return new Map(
            stored.map((item) => {
                // Carts saved before bundles were told apart have no type: they held offers.
                const ref: StoredCartItem = {
                    type: item.type === 'bundle' ? 'bundle' : 'offer',
                    id: item.id,
                    quantity: item.quantity,
                };
                return [getCartKey(ref), ref];
            }),
        );
    } catch {
        return new Map();
    }
}

function persistCart(quantities: Quantities): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(quantities.values())));
    } catch {
        // localStorage unavailable — silently skip
    }
}

function mapCartApiItem(item: CartApiItem, productsByKey: Map<string, Product>): CartItem {
    const ref = { type: item.type, id: item.sellable_id };
    const key = getCartKey(ref);
    return {
        ...ref,
        key,
        title: item.name,
        description: '',
        price: item.unit_price.amount,
        finalPrice: item.final_unit_price.amount,
        subtotal: parseFloat(item.subtotal.amount),
        total: parseFloat(item.total.amount),
        imageSrc: getImageUrl(item.main_image),
        quantity: item.qty,
        orderable: item.orderable,
        status: item.status,
        // The cart has no slug: it comes from the site's products; without it the offer page redirects to its canonical URL.
        href: getCatalogItemPath({ ...ref, slug: productsByKey.get(key)?.slug }),
    };
}

function mapCartResponseItems(items: CartApiItem[], productsByKey: Map<string, Product>): Map<string, CartItem> {
    return new Map(
        items.map((item) => {
            const mapped = mapCartApiItem(item, productsByKey);
            return [mapped.key, mapped];
        }),
    );
}

function mapGuestItems(quantities: Quantities, productsByKey: Map<string, Product>): Map<string, CartItem> {
    const result = new Map<string, CartItem>();
    quantities.forEach(({ type, id, quantity }, key) => {
        const offer = productsByKey.get(key);
        if (!offer) {
            return;
        }
        const subtotal = parseFloat(offer.price.amount) * quantity;
        result.set(key, {
            type,
            id,
            key,
            title: offer.name,
            description: offer.description ?? '',
            price: offer.price.amount,
            finalPrice: offer.price.amount,
            subtotal,
            total: subtotal,
            imageSrc: getImageUrl(offer.main_image),
            quantity,
            orderable: true,
            status: null,
            href: getCatalogItemPath(offer),
        });
    });
    return result;
}

function pickKnownProducts(quantities: Quantities, productsByKey: Map<string, Product>): Quantities {
    return new Map(Array.from(quantities).filter(([key]) => productsByKey.has(key)));
}

interface CartProviderProps {
    children: React.ReactNode;
}

/** The cart for the whole app (`useCart`): loads it and adds, updates and removes items through the cart server actions. */
export function CartProvider({ children }: CartProviderProps): React.ReactElement {
    const { t } = useTranslation();
    const { products, region } = useSiteConfig();
    const { isAuthenticated, isInitializing, loginVersion, logout } = useAuth();

    const productsByKey = useMemo(
        () => new Map(products.flatMap((group) => group.items).map((product) => [getCartKey(product), product])),
        [products],
    );

    const [quantities, setQuantities] = useState<Quantities>(() => new Map());
    const [serverItems, setServerItems] = useState<Map<string, CartItem> | null>(null);
    const [isStoreLoaded, setIsStoreLoaded] = useState(false);
    const [isServerSettled, setIsServerSettled] = useState(false);
    const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());

    const items = useMemo(
        () => serverItems ?? mapGuestItems(quantities, productsByKey),
        [serverItems, quantities, productsByKey],
    );

    const isReady = isStoreLoaded && !isInitializing && (!isAuthenticated || isServerSettled);

    const quantitiesRef = useRef(quantities);
    quantitiesRef.current = quantities;

    const requestSeqRef = useRef(0);
    const hasLoadedRef = useRef(false);

    useEffect(() => {
        const stored = readStoredCart();
        const known = pickKnownProducts(stored, productsByKey);
        if (known.size !== stored.size) {
            persistCart(known);
        }
        setQuantities(known);
        setIsStoreLoaded(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleActionFailure = (result: CartActionResult): void => {
        if (result.success === true) {
            return;
        }
        if (result.status === 401) {
            void logout();
            return;
        }
        toast.error(t('error.general'));
    };

    const applyServerResponse = (res: CartResponse): void => {
        const mapped = mapCartResponseItems(res.items, productsByKey);
        const nextQuantities: Quantities = new Map(
            Array.from(mapped.values(), (item) => [
                item.key,
                { type: item.type, id: item.id, quantity: item.quantity },
            ]),
        );
        setServerItems(mapped);
        setQuantities(nextQuantities);
        persistCart(nextQuantities);
    };

    useEffect(() => {
        if (isInitializing || !isAuthenticated || hasLoadedRef.current) {
            return;
        }
        hasLoadedRef.current = true;

        const seq = ++requestSeqRef.current;
        getCart()
            .then((result) => {
                if (seq !== requestSeqRef.current) {
                    return;
                }
                if (result.success === false) {
                    handleActionFailure(result);
                    return;
                }
                applyServerResponse(result.data);
            })
            .catch(() => toast.error(t('error.general')))
            .finally(() => setIsServerSettled(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInitializing, isAuthenticated]);

    useEffect(() => {
        if (loginVersion === 0) {
            return;
        }
        hasLoadedRef.current = true;

        const guestQuantities = Array.from(pickKnownProducts(quantitiesRef.current, productsByKey).values());
        const seq = ++requestSeqRef.current;

        void (async () => {
            try {
                const currentResult = await getCart();
                if (currentResult.success === false) {
                    handleActionFailure(currentResult);
                    return;
                }
                const current = currentResult.data;
                const serverByKey = new Map(
                    current.items.map((item) => [getCartKey({ type: item.type, id: item.sellable_id }), item]),
                );

                const toAdd: CartItemPayload[] = [];
                const toSet: CartItemPayload[] = [];

                for (const { type, id, quantity } of guestQuantities) {
                    const serverItem = serverByKey.get(getCartKey({ type, id }));
                    if (!serverItem) {
                        toAdd.push({ type, id, qty: quantity });
                    } else if (serverItem.qty !== quantity) {
                        toSet.push({ type, id, qty: quantity });
                    }
                }

                let final: CartResponse = current;
                if (toAdd.length > 0) {
                    const addResult = await addCartItems(toAdd);
                    if (addResult.success === false) {
                        handleActionFailure(addResult);
                        return;
                    }
                    final = addResult.data;
                }
                if (toSet.length > 0) {
                    const setResult = await updateCartItems(toSet);
                    if (setResult.success === false) {
                        handleActionFailure(setResult);
                        return;
                    }
                    final = setResult.data;
                }

                if (seq !== requestSeqRef.current) {
                    return;
                }
                applyServerResponse(final);
            } catch {
                toast.error(t('error.general'));
            } finally {
                setIsServerSettled(true);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loginVersion]);

    useEffect(() => {
        if (!isAuthenticated) {
            hasLoadedRef.current = false;
            setServerItems(null);
            setIsServerSettled(false);
        }
    }, [isAuthenticated]);

    const mutateGuest = (updater: (prev: Quantities) => Quantities): void => {
        const next = updater(quantitiesRef.current);
        persistCart(next);
        setQuantities(next);
    };

    const setRefsPending = (refs: CartItemRef[], pending: boolean): void => {
        setPendingKeys((prev) => {
            const next = new Set(prev);
            refs.forEach((ref) => (pending ? next.add(getCartKey(ref)) : next.delete(getCartKey(ref))));
            return next;
        });
    };

    const runAuthorizedMutation = async (
        refs: CartItemRef[],
        request: () => Promise<CartActionResult>,
        onSuccess?: () => void,
    ): Promise<void> => {
        const seq = ++requestSeqRef.current;
        setRefsPending(refs, true);
        try {
            const result = await request();
            // The server has applied it even when a newer request makes this response stale.
            if (result.success) {
                onSuccess?.();
            }
            if (seq !== requestSeqRef.current) {
                return;
            }
            if (result.success === false) {
                handleActionFailure(result);
                return;
            }
            applyServerResponse(result.data);
        } catch {
            toast.error(t('error.general'));
        } finally {
            setRefsPending(refs, false);
        }
    };

    const trackAddToCart = (): void => reachGoal(region.code, ANALYTICS_GOALS.ADD_TO_CART);

    const addItem = ({ type, id }: CartItemRef): void => {
        const key = getCartKey({ type, id });
        if (isAuthenticated) {
            void runAuthorizedMutation([{ type, id }], () => addCartItems([{ type, id, qty: 1 }]), trackAddToCart);
            return;
        }

        if (!productsByKey.has(key)) {
            return;
        }

        mutateGuest((prev) => new Map(prev).set(key, { type, id, quantity: (prev.get(key)?.quantity ?? 0) + 1 }));
        trackAddToCart();
    };

    const removeItems = (refs: CartItemRef[]): void => {
        if (refs.length === 0) {
            return;
        }

        if (isAuthenticated) {
            void runAuthorizedMutation(refs, () => removeCartItems(refs.map(({ type, id }) => ({ type, id }))));
            return;
        }

        mutateGuest((prev) => {
            const next = new Map(prev);
            refs.forEach((ref) => next.delete(getCartKey(ref)));
            return next;
        });
    };

    const removeItem = (ref: CartItemRef): void => {
        removeItems([ref]);
    };

    const updateQuantity = ({ type, id }: CartItemRef, quantity: number): void => {
        if (quantity <= 0) {
            removeItem({ type, id });
            return;
        }

        const key = getCartKey({ type, id });
        const onIncrease = quantity > (items.get(key)?.quantity ?? 0) ? trackAddToCart : undefined;

        if (isAuthenticated) {
            void runAuthorizedMutation(
                [{ type, id }],
                () => updateCartItems([{ type, id, qty: quantity }]),
                onIncrease,
            );
            return;
        }

        if (!quantitiesRef.current.has(key)) {
            return;
        }
        mutateGuest((prev) => new Map(prev).set(key, { type, id, quantity }));
        onIncrease?.();
    };

    return (
        <CartContext.Provider value={{ items, isReady, pendingKeys, addItem, removeItem, removeItems, updateQuantity }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart(): CartContextValue {
    const ctx = useContext(CartContext);
    if (!ctx) {
        throw new Error('useCart must be used within CartProvider');
    }
    return ctx;
}
